import { useEffect, useMemo, useState } from 'react';
import {
  BalanceType,
  BatchCreateBalancesPaymentRequestsRequest,
  BatchCreateBalancesPaymentRequestsRequest_ExistingBalanceRequestOption,
  BatchCreateBalancesPaymentRequestsRequest_NewBalanceCreateRequestOption,
  BatchCreateBalancesPaymentRequestsRequest_PaymentRequests,
  BatchCreateBalancesPaymentRequestsRequest_UpdatedBalanceRequestOption,
  GetPatientsBalanceResponse_PatientBalance,
} from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { Commands1 } from '@frontend/api-commands';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { ContentLoader, Modal, ModalControlModalProps, useAlert, useForm } from '@frontend/design-system';
import { CollectionColumn } from '../CollectionsList/generate-columns';
import { BalancesForm } from './components/balances-form';
import { Result } from './components/bulk-request-result';

type BulkPaymentRequestModalProps = {
  modalProps: ModalControlModalProps;
  selected?: CollectionColumn[];
};
export const BulkPaymentRequestModal = ({ modalProps, selected }: BulkPaymentRequestModalProps) => {
  const { t } = useTranslation('payments');
  const alert = useAlert();
  const { invalidateQueries } = Commands1._QueryUpdaters.useQueryUpdaters();
  const { selectedLocationIds } = useAppScopeStore();
  const [result, setResult] = useState<{
    totalSuccessCount: number;
    changedBalanceSuccessCount: number;
    failedCount: number;
  } | null>(null);

  const { data: collections } = Commands1.Queries.useGetPatientsBalanceQuery({
    request: {
      locationIds: selectedLocationIds,
    },
    options: {
      onError: () => {
        alert.error('Error fetching collections');
      },
      enabled: !selected && modalProps.show,
    },
  });

  const { mutate: createPaymentRequest, isLoading } = Commands1.Mutations.useBatchCreateBalancesPaymentRequestsMutation(
    {
      options: {
        onSuccess: (data) => {
          invalidateQueries({ endpointName: 'GetPatientsBalance' });
          const changedBalancePatients = patients.filter((p) => p.balanceType === BalanceType.BALANCE_TYPE_UPDATE);
          const changedBalancePatientIds = new Set(changedBalancePatients.map((p) => p.patientId));

          const changedBalanceSuccessCount =
            data.success?.filter((s) => changedBalancePatientIds.has(s.patientId ?? '')).length || 0;

          setResult({
            failedCount: data.failure?.length || 0,
            totalSuccessCount: data.success?.length || 0,
            changedBalanceSuccessCount,
          });
        },
        onError: () => {
          alert.error(t('Error creating payment requests'));
          modalProps.onClose();
        },
      },
    }
  );

  const patients = useMemo(() => selected || collections?.patientBalances || [], [selected, collections]);
  const balancesCount = useMemo(() => {
    return (
      patients?.reduce(
        (acc, { balanceType }) => {
          switch (balanceType) {
            case BalanceType.BALANCE_TYPE_NEW:
              acc.newBalances++;
              break;
            case BalanceType.BALANCE_TYPE_UPDATE:
              acc.changedBalances++;
              break;
            case BalanceType.BALANCE_TYPE_EXISTS:
              acc.existingBalances++;
              break;
          }
          return acc;
        },
        { newBalances: 0, changedBalances: 0, existingBalances: 0 }
      ) || {}
    );
  }, [patients]);

  const transformPatientsData = (patients: GetPatientsBalanceResponse_PatientBalance[]) => {
    const result: Record<string, BatchCreateBalancesPaymentRequestsRequest_PaymentRequests> = {};

    patients.forEach(({ patientId, balance, balanceType, locationIds, provider }) => {
      locationIds?.forEach((locationId) => {
        if (!result[locationId]) {
          result[locationId] = { requests: [] };
        }

        result[locationId].requests?.push({
          patientId: patientId,
          amount: balance,
          locationId: locationId,
          balanceType: balanceType,
          provider,
        });
      });
    });

    return result;
  };

  const { formProps, getFieldProps, values, reset } = useForm({
    fields: {
      newBalance: {
        type: 'dropdown',
        required: true,
        value:
          BatchCreateBalancesPaymentRequestsRequest_NewBalanceCreateRequestOption.NEW_BALANCE_CREATE_REQUEST_OPTION_CREATE,
      },
      changedBalance: {
        type: 'dropdown',
        required: true,
        value:
          BatchCreateBalancesPaymentRequestsRequest_UpdatedBalanceRequestOption.UPDATED_BALANCE_REQUEST_OPTION_REPLACE,
      },
      existingBalance: {
        type: 'dropdown',
        required: true,
        value:
          BatchCreateBalancesPaymentRequestsRequest_ExistingBalanceRequestOption.EXISTING_BALANCE_REQUEST_OPTION_IGNORE,
      },
    },
    onSubmit: async (values) => {
      const payload: BatchCreateBalancesPaymentRequestsRequest = {
        requests: selected?.length
          ? transformPatientsData(patients)
          : selectedLocationIds.reduce((o, k) => ({ ...o, [k]: {} }), {}),
        createRequestsForAll: selected?.length ? false : true,
        createNewRequestOption:
          values.newBalance as BatchCreateBalancesPaymentRequestsRequest_NewBalanceCreateRequestOption,
        existingBalanceRequestOption:
          values.existingBalance as BatchCreateBalancesPaymentRequestsRequest_ExistingBalanceRequestOption,
        updatedBalanceRequestOption:
          values.changedBalance as BatchCreateBalancesPaymentRequestsRequest_UpdatedBalanceRequestOption,
      };
      createPaymentRequest(payload);
    },
  });

  const shouldDisableSubmit = useMemo(() => {
    if (!patients.length) return true;

    const newBalanceActionPossible =
      !!balancesCount.newBalances &&
      values.newBalance !==
        BatchCreateBalancesPaymentRequestsRequest_NewBalanceCreateRequestOption.NEW_BALANCE_CREATE_REQUEST_OPTION_IGNORE;
    const changedBalanceActionPossible =
      !!balancesCount.changedBalances &&
      values.changedBalance !==
        BatchCreateBalancesPaymentRequestsRequest_UpdatedBalanceRequestOption.UPDATED_BALANCE_REQUEST_OPTION_IGNORE;
    const existingBalanceActionPossible =
      !!balancesCount.existingBalances &&
      values.existingBalance !==
        BatchCreateBalancesPaymentRequestsRequest_ExistingBalanceRequestOption.EXISTING_BALANCE_REQUEST_OPTION_IGNORE;

    if (!newBalanceActionPossible && !changedBalanceActionPossible && !existingBalanceActionPossible) {
      return true;
    }

    return false;
  }, [balancesCount, values]);

  useEffect(() => {
    // RESET states when modal is opened
    if (modalProps.show) {
      setResult(null);
      reset();
    }
  }, [modalProps.show]);

  return (
    <Modal {...modalProps} minWidth={600}>
      <Modal.Header onClose={modalProps.onClose}>{t('Bulk Create Payment Requests')}</Modal.Header>
      {isLoading ? (
        <Modal.Body css={{ minHeight: 200 }}>
          <ContentLoader show={isLoading} />
        </Modal.Body>
      ) : result ? (
        <Result
          {...result}
          totalCount={patients.length}
          totalChangedBalanceCount={balancesCount?.changedBalances}
          onClose={modalProps.onClose}
        />
      ) : (
        <BalancesForm
          balancesCount={balancesCount}
          getFieldProps={getFieldProps}
          onClose={modalProps.onClose}
          onSubmit={formProps.onSubmit}
          disabled={shouldDisableSubmit}
        />
      )}
    </Modal>
  );
};
